import type { Persister } from '@tanstack/query-persist-client-core'
import type { ReactNode } from 'react'
import { useLayoutEffect, useRef, useState } from 'react'
import { captureRequestScope } from '@/lib/api/request-scope'
import { createPersister } from '@/lib/cache/persister'
import {
  isTodayDashboardQuery,
  shouldPersistQuery,
} from '@/lib/cache/query-filter'
import {
  isOwnedOfflineEnvelope,
  shouldDehydrateOfflineMutation,
} from '@/lib/offline/offline-queue'
import {
  initializeOfflineQueue,
  resumeOfflineWrites,
} from '@/lib/offline/online-bridge'
import { queryClient } from '@/lib/queryClient'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { persistQueryClientSubscribe } from '@tanstack/query-persist-client-core'
import {
  hydrate,
  IsRestoringProvider,
  QueryClientProvider,
} from '@tanstack/react-query'

import { restoreInScope } from '@scrambl/shared/session-scope'

/**
 * How old cached data can be before we skip restore entirely and just fetch fresh.
 * This prevents showing yesterday's data for 1-2 seconds on app open.
 * 5 minutes = if you closed the tab and reopened quickly, you get instant render.
 * Anything older = normal loading states, but with fresh data.
 */
const STALE_THRESHOLD = 1000 * 60 * 5 // 5 minutes

/**
 * Today-dashboard keys get a much longer restore window than everything else.
 * Today is the landing surface and every widget gates its own skeleton on its
 * own query, so a cold open past the 5-minute window is a full wall of shimmer.
 * Restoring the last-known dashboard paints instantly and revalidates silently.
 * Past 24h the snapshot is dropped outright — a day-old dashboard is a lie.
 */
const TODAY_DASHBOARD_MAX_AGE = 1000 * 60 * 60 * 24 // 24 hours

/**
 * Outer cap handed to persistQueryClientRestore. It has to cover the widest
 * window any key is allowed to use (the Today one), because it is applied to
 * the persisted blob as a whole; per-key freshness is enforced by the guard
 * below.
 */
const MAX_AGE = TODAY_DASHBOARD_MAX_AGE

/**
 * Snapshot format version. Bumped from `owned-v2` when the writer's user id
 * became part of the buster (see `cacheBusterFor`), which also invalidates
 * every bucket written before the fix for cross-account leakage.
 */
const CACHE_BUSTER_VERSION = 'owned-v3'

/**
 * The buster stamps the snapshot with the user whose session wrote it.
 * `persistQueryClientRestore` drops a snapshot whose buster differs from the
 * restoring options', so a bucket that somehow holds another account's rows
 * (any future race between sign-in and the in-memory clear below) is refused
 * on restore instead of hydrated. The storage key already carries the user
 * id; this guard is the one that holds even if the key resolution is wrong.
 */
export function cacheBusterFor(userId: string): string {
  return `${CACHE_BUSTER_VERSION}:${userId}`
}

/**
 * Per-tab record of which user this tab's in-memory client last belonged to.
 * sessionStorage is deliberate: the leak this guards against is one tab
 * learning about a sign-out/sign-in that happened in another tab of the same
 * profile. Its own React tree never saw an A -> B scope switch, only
 * A -> undefined -> B, and the client still held A's rows.
 */
const CACHE_OWNER_KEY = 'scrambl-cache-owner'

function readCacheOwner(): string | null {
  try {
    return window.sessionStorage.getItem(CACHE_OWNER_KEY)
  } catch {
    return null
  }
}

function writeCacheOwner(userId: string | null): void {
  try {
    if (userId) window.sessionStorage.setItem(CACHE_OWNER_KEY, userId)
    else window.sessionStorage.removeItem(CACHE_OWNER_KEY)
  } catch {
    // Storage unavailable (private mode quirks): the sign-out clear below
    // still runs, only the cross-tab belt-and-braces check is lost.
  }
}

function dropInMemoryCache(): void {
  void queryClient.cancelQueries()
  queryClient.clear()
}

/**
 * Wraps a real persister to skip restore if the cached data is too stale.
 * Still persists on every save (so fresh data is always written), but on restore
 * it checks the timestamp and drops what has aged out:
 * - past 5 minutes, only Today-dashboard keys survive
 * - past 24 hours, nothing survives
 */
function createFreshnessGuard(inner: Persister): Persister {
  return {
    persistClient: inner.persistClient,
    removeClient: inner.removeClient,
    restoreClient: async () => {
      const cached = await inner.restoreClient()
      if (!cached) return undefined

      // Paused writes are never subject to the read-cache freshness policy.
      // Those windows exist so nobody is shown yesterday's numbers as if they
      // were today's — a reason that has nothing to do with a change the user
      // made and is still waiting on. Dropping the blob wholesale, as this
      // guard used to, would silently discard queued work; the queue enforces
      // its own, much longer, expiry (OFFLINE_QUEUE_MAX_AGE_MS).
      const mutations = cached.clientState.mutations ?? []

      const age = Date.now() - cached.timestamp
      if (age > TODAY_DASHBOARD_MAX_AGE) {
        console.info(
          `[QueryCache] Skipping restore — cache is ${Math.round(age / 1000)}s old (max: ${TODAY_DASHBOARD_MAX_AGE / 1000}s)`,
        )
        return mutations.length > 0
          ? { ...cached, clientState: { ...cached.clientState, queries: [] } }
          : undefined
      }

      const isPastGeneralWindow = age > STALE_THRESHOLD
      const queries = cached.clientState.queries.filter(query => {
        if (!shouldPersistQuery(query.queryKey)) return false
        // Everything but the Today dashboard goes back to normal loading
        // states once the general freshness window has passed.
        return !isPastGeneralWindow || isTodayDashboardQuery(query.queryKey)
      })

      if (queries.length === 0 && mutations.length === 0) return undefined

      console.info(
        `[QueryCache] Restoring ${queries.length} ${isPastGeneralWindow ? 'Today-dashboard ' : ''}queries and ${mutations.length} pending writes — cache is ${Math.round(age / 1000)}s old`,
      )
      return {
        ...cached,
        clientState: {
          ...cached.clientState,
          queries,
        },
      }
    },
  }
}

// Initialised at module scope, not in an effect. `hydrate()` resolves a
// restored mutation's options — including its `mutationFn` — the moment it
// rebuilds it from storage, and restore begins in this provider's first effect.
// Defaults registered later would arrive after that, and every rehydrated write
// would come back inert.
initializeOfflineQueue(queryClient)

export function QueryCacheProvider({ children }: { children: ReactNode }) {
  const userId = useAuth(s => s.user?.id)
  const orgId = useOrganizationStore(s => s.currentOrganization?.id)
  const relationshipId = useOrganizationStore(
    s => s.externalOrgRelationship?.id,
  )
  const [isRestoring, setIsRestoring] = useState(false)

  // Scope the cache per user AND org: query keys are org-agnostic (the org is
  // a request header), so an org-agnostic cache restored after an org switch
  // would serve the previous org's rows under identical query keys.
  const scopeKey =
    userId && orgId
      ? JSON.stringify([userId, orgId, relationshipId])
      : undefined
  const prevScopeKeyRef = useRef<string | undefined>(undefined)
  // Held so sign-out can wipe the stored snapshot, not just stop writing to it.
  const persisterRef = useRef<Persister | null>(null)

  useLayoutEffect(() => {
    const prevScopeKey = prevScopeKeyRef.current
    prevScopeKeyRef.current = scopeKey

    // In-session user/org switch: the in-memory cache still holds the previous
    // scope's rows under org-agnostic query keys. Drop it all before restoring
    // or subscribing, otherwise those rows both render in the new org and get
    // persisted into the new org's storage bucket (which would then serve
    // cross-org data on the next boot). The org-switch removeQueries list only
    // covers known keys — this catches everything.
    if (prevScopeKey && prevScopeKey !== scopeKey) {
      dropInMemoryCache()
    }

    // `scopeKey` is only defined when `userId` is; the second test is for
    // the type checker.
    if (!scopeKey || !userId) {
      // Sign-out. AuthProvider.handleLogout clears the query cache itself,
      // but the auth-check paths that discover a dead session (a 401, or the
      // periodic /auth/me after another tab signed out) only null the user.
      // Clear here too, so no path leaves the previous account's rows in
      // memory for the next sign-in to persist under its own bucket.
      // Stopping the subscription isn't enough either — the last-known
      // dashboard is still sitting in IndexedDB/SQLite, and the next person to
      // sign in on this machine must never see it. Wipe it outright.
      const orphaned = persisterRef.current
      persisterRef.current = null
      if (prevScopeKey) {
        dropInMemoryCache()
        writeCacheOwner(null)
      }
      if (prevScopeKey && orphaned) {
        void Promise.resolve(orphaned.removeClient()).catch(
          (error: unknown) => {
            console.warn(
              '[QueryCache] Failed to clear persisted cache on sign-out',
              error,
            )
          },
        )
      }
      setIsRestoring(false)
      return
    }

    // Fresh scope with no in-tree predecessor (first sign-in in this tab, or
    // a sign-in this tab learned about from an auth check). If this tab's
    // client last belonged to someone else, drop it before the new user's
    // persister can dehydrate it. A first boot has no marker and holds only
    // this user's pre-scope queries, so it pays no refetch cost.
    if (!prevScopeKey) {
      const previousOwner = readCacheOwner()
      if (previousOwner && previousOwner !== userId) dropInMemoryCache()
    }
    writeCacheOwner(userId)

    const session = captureRequestScope()
    let cancelled = false
    let unsubscribe: (() => void) | undefined
    setIsRestoring(true)

    const initializePersistence = async () => {
      const createdPersister = await createPersister(scopeKey)
      if (cancelled || !createdPersister) return
      persisterRef.current = createdPersister

      const options = {
        queryClient,
        persister: createFreshnessGuard({
          ...createdPersister,
          persistClient: client =>
            cancelled || session.signal.aborted
              ? Promise.resolve()
              : createdPersister.persistClient(client),
        }),
        maxAge: MAX_AGE,
        buster: cacheBusterFor(userId),
        dehydrateOptions: {
          // Only a settled, successful query is worth restoring. A query that
          // errored keeps its previous `data` in React Query's state, and a
          // 401/403 is exactly the case where that data must not outlive the
          // session (queryClient.ts also evicts it); an in-flight query's
          // snapshot is whatever it was before the refetch started.
          shouldDehydrateQuery: (query: {
            state: { status: string; fetchStatus: string; error: unknown }
            queryKey: readonly unknown[]
          }) =>
            query.state.status === 'success' &&
            query.state.error == null &&
            query.state.fetchStatus === 'idle' &&
            shouldPersistQuery(query.queryKey),
          /**
           * Paused writes ride along with the read cache so a change made on a
           * dead connection survives a reload or a closed lid. The predicate is
           * deliberately narrow — paused AND carrying an `['offline', …]`
           * mutation key — which is what keeps financial and destructive
           * writes out of the queue by construction rather than by anyone
           * remembering to exclude them.
           *
           * A persisted mutation is inert without a matching
           * `setMutationDefaults`; those are registered by
           * `initializeOfflineQueue` before restore runs, below.
           */
          shouldDehydrateMutation: shouldDehydrateOfflineMutation,
        },
      }

      const restored = await restoreInScope({
        read: () => options.persister.restoreClient(),
        isCurrent: () => !cancelled && !session.signal.aborted,
        apply: snapshot => {
          if (snapshot && snapshot.buster === options.buster) {
            hydrate(queryClient, {
              ...snapshot.clientState,
              mutations: snapshot.clientState.mutations.filter(
                mutation =>
                  isOwnedOfflineEnvelope(mutation.state.variables) &&
                  mutation.state.variables.ownerId === userId,
              ),
            })
          }
          unsubscribe = persistQueryClientSubscribe(options)
        },
      })
      if (!restored) return
      await resumeOfflineWrites(queryClient)
      // Background-refresh stale restored data. Subscribing first also ensures
      // the current cache is saved even when auth/org resolved after mount.
      if (!cancelled && !session.signal.aborted)
        await queryClient.invalidateQueries({ stale: true })
    }

    void initializePersistence()
      .catch((error: unknown) => {
        console.warn('[QueryCache] Failed to initialize persistence', error)
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false)
      })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [scopeKey, userId])

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={isRestoring}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  )
}
